import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase-client';
import { useAuth } from '../../lib/AuthContext';

// Types
export interface Subscription {
    id: string;
    user_id: string;
    lemon_squeezy_subscription_id: string | null;
    lemon_squeezy_customer_id: string | null;
    plan_type: 'free' | 'professional' | 'enterprise';
    plan_name: string;
    status: 'active' | 'canceled' | 'past_due' | 'expired' | 'paused' | 'on_trial';
    price_amount: number | null;
    currency: string;
    billing_interval: 'month' | 'year' | null;
    monthly_render_limit: number;
    renders_used_current_period: number;
    current_period_start: string | null;
    current_period_end: string | null;
    renews_at: string | null;
    canceled_at: string | null;
    ends_at: string | null;
    created_at: string;
}

export interface Invoice {
    id: string;
    invoice_number: string;
    invoice_date: string;
    amount: number;
    tax_amount: number;
    total_amount: number;
    currency: string;
    status: 'paid' | 'pending' | 'refunded' | 'void';
    invoice_url: string | null;
}

interface UseSubscriptionReturn {
    subscription: Subscription | null;
    invoices: Invoice[];
    loading: boolean;
    error: string | null;
    // Computed values
    creditsAvailable: number;
    creditsLimit: number;
    usagePercent: number;
    planDisplayName: string;
    priceDisplay: string;
    renewsAt: string | null;
    isActive: boolean;
    isCanceled: boolean;
    // Actions
    refreshSubscription: () => Promise<void>;
    getLemonSqueezyCheckoutUrl: (planType: 'starter' | 'pro' | 'agency') => string;
    getLemonSqueezyPortalUrl: () => string | null;
}

// Fallback checkout URLs (used if plans table is empty or unreachable)
const FALLBACK_CHECKOUT_URLS: Record<string, string> = {
    starter: 'https://wysko.lemonsqueezy.com/checkout/buy/d27a5d53-1c60-4354-ac2c-8b362e46aaff',
    pro: 'https://wysko.lemonsqueezy.com/checkout/buy/5970d76b-d0d9-4e5e-a5b3-1f70beca1fba',
    agency: 'https://wysko.lemonsqueezy.com/checkout/buy/366b4326-3a66-41bd-9137-c566297d212e',
};

interface Plan {
    id: string;
    name: string;
    price: number;
    credits: number;
    checkout_url: string | null;
    is_active: boolean;
}

export function useSubscription(): UseSubscriptionReturn {
    const { user, profile } = useAuth();
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [plans, setPlans] = useState<Plan[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadSubscription = async () => {
        if (!user?.id) {
            setLoading(false);
            return;
        }

        try {
            // Load active subscription
            // Note: Using 'as any' until database.types.ts is regenerated with new tables
            const { data: subData, error: subError } = await (supabase as any)
                .from('subscriptions')
                .select('*')
                .eq('user_id', user.id)
                .in('status', ['active', 'on_trial', 'past_due', 'canceled'])
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (subError && subError.code !== 'PGRST116') {
                // PGRST116 = no rows returned, which is fine for new users
                console.error('Error loading subscription:', subError);
            }

            setSubscription(subData as Subscription | null);

        } catch (err) {
            console.error('Subscription load error:', err);
            setError('Nie udało się załadować danych subskrypcji');
        }
    };

    const loadInvoices = async () => {
        if (!user?.id) return;

        try {
            // Note: Using 'as any' until database.types.ts is regenerated with new tables
            const { data, error: invoiceError } = await (supabase as any)
                .from('invoices')
                .select('*')
                .eq('user_id', user.id)
                .order('invoice_date', { ascending: false })
                .limit(20);

            if (invoiceError) {
                console.error('Error loading invoices:', invoiceError);
            } else {
                setInvoices((data || []) as Invoice[]);
            }
        } catch (err) {
            console.error('Invoice load error:', err);
        }
    };

    const loadPlans = async () => {
        try {
            const { data, error: plansError } = await (supabase as any)
                .from('plans')
                .select('id, name, price, credits, checkout_url, is_active')
                .eq('is_active', true)
                .order('price', { ascending: true });

            if (!plansError && data) {
                setPlans(data as Plan[]);
            }
        } catch (err) {
            console.error('Plans load error:', err);
        }
    };

    const refreshSubscription = async () => {
        setLoading(true);
        await Promise.all([loadSubscription(), loadInvoices(), loadPlans()]);
        setLoading(false);
    };

    useEffect(() => {
        refreshSubscription();
    }, [user?.id]);

    // Computed values
    const creditsAvailable = profile?.available_credits ?? 0;

    // Build credits map from DB plans (with hardcoded fallback for trial/free)
    const planCreditsMap: Record<string, number> = { trial: 2, free: 2 };
    for (const plan of plans) {
        planCreditsMap[plan.id] = plan.credits;
    }

    // Get credits limit from subscription or plan type
    const planCreditsLimit = subscription?.monthly_render_limit
        || planCreditsMap[subscription?.plan_type || '']
        || planCreditsMap[profile?.subscription_status || '']
        || 2;

    // If admin has granted bonus credits beyond plan limit, show that as the limit
    const creditsLimit = Math.max(creditsAvailable, planCreditsLimit);

    const usagePercent = creditsLimit > 0
        ? Math.min(((creditsLimit - creditsAvailable) / creditsLimit) * 100, 100)
        : 0;

    const planDisplayName = subscription?.plan_name
        || (profile?.subscription_status === 'trial' ? 'Trial'
            : profile?.subscription_status === 'active' ? 'Plan Aktywny'
                : 'Plan Darmowy');

    // Build prices map from DB plans
    const planPricesMap: Record<string, number> = {};
    for (const plan of plans) {
        planPricesMap[plan.id] = plan.price;
    }

    const priceDisplay = subscription?.price_amount && subscription.price_amount > 0
        ? `${subscription.price_amount} PLN/mies.`
        : (subscription?.plan_type && planPricesMap[subscription.plan_type]
            ? `${planPricesMap[subscription.plan_type]} PLN/mies.`
            : '0 PLN');

    const isActive = subscription?.status === 'active' || subscription?.status === 'on_trial';
    const isCanceled = subscription?.status === 'canceled';

    // Actions
    const getLemonSqueezyCheckoutUrl = (planType: 'starter' | 'pro' | 'agency'): string => {
        // Prefer checkout URL from DB, fallback to hardcoded
        const dbPlan = plans.find(p => p.id === planType);
        const baseUrl = dbPlan?.checkout_url || FALLBACK_CHECKOUT_URLS[planType];
        if (!baseUrl) {
            console.warn('Lemon Squeezy checkout URL not configured for plan:', planType);
            return '#';
        }
        // Add user ID as custom data
        const params = new URLSearchParams({
            'checkout[custom][user_id]': user?.id || '',
            'checkout[email]': user?.email || '',
        });
        return `${baseUrl}?${params.toString()}`;
    };

    const getLemonSqueezyPortalUrl = (): string | null => {
        if (!subscription?.lemon_squeezy_customer_id) return null;
        // Lemon Squeezy customer portal — store-specific billing page
        // Pre-fill email so Lemon Squeezy can identify the customer
        const params = new URLSearchParams({ email: user?.email || '' });
        return `https://wysko.lemonsqueezy.com/billing?${params.toString()}`;
    };

    return {
        subscription,
        invoices,
        loading,
        error,
        creditsAvailable,
        creditsLimit,
        usagePercent,
        planDisplayName,
        priceDisplay,
        renewsAt: subscription?.renews_at || null,
        isActive,
        isCanceled,
        refreshSubscription,
        getLemonSqueezyCheckoutUrl,
        getLemonSqueezyPortalUrl,
    };
}
